import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Languages } from "lucide-react";

interface LanguageOption {
  code: string;
  label: string;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "en", label: "English" },
  { code: "sp", label: "Spanish" },
];

interface LanguageSwitcherProps {
  className?: string;
  align?: "start" | "center" | "end";
  size?: "sm" | "default" | "icon";
  variant?: "default" | "minimal";
}

export function LanguageSwitcher({
  className,
  size = "default",
  variant = "default",
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [selected, setSelected] = useState(i18n.resolvedLanguage);

  const handleLanguageChange = async (lang: string) => {
    setSelected(lang);
    await i18n.changeLanguage(lang);
  };

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value;
    await handleLanguageChange(lang);
  };

  // Minimal variant with dropdown (for sidebar and login page)
  if (variant === "minimal") {
    // Icon-only mode for collapsed sidebar
    if (size === "icon") {
      return (
        <div className={cn("w-full", className)}>
          <div className="relative group">
            <Languages className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none group-hover:text-foreground transition-colors z-10" />
            <select
              className="w-full pl-8 pr-8 py-2 text-xs font-semibold border-2 border-input rounded-lg bg-card text-foreground cursor-pointer hover:border-primary/50 hover:bg-muted/30 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all shadow-sm hover:shadow-md appearance-none"
              value={selected}
              onChange={handleChange}
              aria-label={t("languageSwitcher.ariaLabel")}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: "right 0.5rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.25em 1.25em",
              }}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.code.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    // Full width mode for expanded sidebar and login page
    return (
      <div className={cn("w-full", className)}>
        <label className="text-xs font-semibold mb-2 flex items-center gap-2 text-foreground/80 uppercase tracking-wide">
          <Languages className="w-4 h-4" />
          {t("languageSwitcher.label")}
        </label>
        <div className="relative group">
          <select
            className="w-full pl-4 pr-10 py-2.5 text-sm font-medium border-2 border-input rounded-lg bg-card text-foreground cursor-pointer hover:border-primary/50 hover:bg-muted/30 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all shadow-sm hover:shadow-md appearance-none"
            value={selected}
            onChange={handleChange}
            aria-label={t("languageSwitcher.ariaLabel")}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.75rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
            }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Default variant with dropdown
  return (
    <div className={cn("w-full", className)}>
      {size !== "icon" && (
        <label className="text-sm font-semibold mb-2 flex items-center gap-2 text-foreground/80 uppercase tracking-wide">
          <Languages className="w-4 h-4" />
          {t("languageSwitcher.label")}
        </label>
      )}
      <div className="relative group">
        {size === "icon" && (
          <Languages className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none group-hover:text-foreground transition-colors z-10" />
        )}
        <select
          className={cn(
            "w-full py-2.5 border-2 border-input rounded-lg bg-card text-foreground font-medium cursor-pointer hover:border-primary/50 hover:bg-muted/30 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all shadow-sm hover:shadow-md appearance-none",
            size === "icon" ? "pl-10 pr-10" : "pl-4 pr-10"
          )}
          value={selected}
          onChange={handleChange}
          aria-label={
            size === "icon" ? t("languageSwitcher.ariaLabel") : undefined
          }
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: "right 0.75rem center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "1.5em 1.5em",
          }}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
